import { AgentAvatar } from "@/components/molecules/agent-avatar";
import type { Agent } from "@/features/agents/types";
import type { AvailableTool } from "@/features/tools/types";
import { useTranslation } from "@/features/utils/i18n";
import { isOnForAgent } from "@/views/backoffice/agents/components/agent-skills";

/** Past this many, the names give way to « 3 agents sur 5 ». */
const MAX_NAMES = 3;
/** A few faces are enough to recognise the team; the label gives the count. */
const MAX_FACES = 4;

/**
 * Which of the company's agents use a module — the one thing the tools page
 * knows that no agent's page does. Only meant for a company with several
 * agents: with one, « on » already says it.
 */
export function ToolAgents({
  item,
  agents,
}: {
  item: AvailableTool;
  agents: Agent[];
}) {
  const { t, i18n } = useTranslation("tools");
  const using = agents.filter((agent) => isOnForAgent(item, agent.id));

  if (using.length === 0) {
    // Off everywhere is what the « Disponibles » section already says; on for
    // the company with every agent opted out is not, and it reads the same.
    if (!item.clientTool?.enabled) return null;
    return (
      <p className="text-muted-foreground mt-1.5 text-xs">
        {t("tools.list.agents.none")}
      </p>
    );
  }

  const label =
    using.length === agents.length
      ? t("tools.list.agents.all")
      : using.length <= MAX_NAMES
        ? new Intl.ListFormat(i18n.language, {
            style: "long",
            type: "conjunction",
          }).format(using.map((agent) => agent.name))
        : t("tools.list.agents.some", {
            count: using.length,
            total: agents.length,
          });

  return (
    <div className="mt-1.5 flex min-w-0 items-center gap-2">
      <div className="flex shrink-0 -space-x-1.5">
        {using.slice(0, MAX_FACES).map((agent) => (
          <AgentAvatar
            key={agent.id}
            avatarId={agent.avatar_id || undefined}
            name={agent.name}
            className="ring-background size-5 rounded-full text-[9px] ring-2"
          />
        ))}
      </div>
      <span className="text-muted-foreground truncate text-xs">{label}</span>
    </div>
  );
}
